import React, { memo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "./styles.module.scss";
import Button from "app/components/Button/ButtonVariant";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import closeIcon from "../../../../public/images/close-icon.svg";
import AthleteListItem from "./AthleteListItem";
import { schedulingFetchGet, schedulingFetchPatch } from 'app/utils/request/scheduling/fetch';
import ToolTip from "app/routes/Scheduling/ToolTip";
import { Spinner } from "app/components/Loading";

const ManageOfferingModal = ({
  product,
  isModalOpen,
  modalHandler,
  stripeIntegrationEnabled: _stripeIntegrationEnabled = true,
  facilityId: _facilityId,
}) => {
  const { t } = useTranslation();
  const [athletes, setAthletes] = useState([]);
  const [fetchData, setFetchData] = useState(true);
  const [markingPaidId, setMarkingPaidId] = useState<number | null>(null);
  // CFTB-3422: athletes starts empty, so without this the empty state ("No
  // athletes assigned yet") rendered while the request was still in flight —
  // on a slow connection with a long roster it read as "this offering is
  // unused" for seconds.
  const [loading, setLoading] = useState(true);

  const fetchHandler = () => {
    setFetchData((prev) => !prev);
  };

  useEffect(() => {
    setLoading(true);
    schedulingFetchGet(`stripe_purchases?stripe_product_id=${product.id}`)
      .then((data) => {
        if (!data) {
          throw new Error('Failed to fetch athletes or invalid response format');
        }
        setAthletes(data);
      })
      .catch((error) => {
        console.error('Error fetching athletes:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fetchData, product.id]);

  const handleMarkAsPaid = async (purchaseId: number) => {
    if (!purchaseId) { return; }
    setMarkingPaidId(purchaseId);
    try {
      await schedulingFetchPatch(`/stripe_purchases/${purchaseId}/mark_paid`);
      toast.success(t("Payment marked as paid"));
      fetchHandler();
    } catch (error) {
      const errorMessage = error?.errors?.[0] || t("An unexpected error has occurred.");
      toast.error(errorMessage);
    } finally {
      setMarkingPaidId(null);
    }
  };

  const handleMarkAsUnpaid = async (purchaseId: number) => {
    if (!purchaseId) { return; }
    setMarkingPaidId(purchaseId);
    try {
      await schedulingFetchPatch(`/stripe_purchases/${purchaseId}/mark_unpaid`);
      toast.success(t("Payment marked as unpaid"));
      fetchHandler();
    } catch (error) {
      const errorMessage = error?.errors?.[0] || t("An unexpected error has occurred.");
      toast.error(errorMessage);
    } finally {
      setMarkingPaidId(null);
    }
  };

  return (
    <ModalLayout isOpen={isModalOpen} onClose={modalHandler} classNames={`${styles.modal} event_form_container`}>
      <div className={styles.modal_header}>
        <div className={styles.titleRow}>
          <h2
            className={styles.truncatedText}
            title={product.title.length > 25 ? product.title : ""}
          >
            {t("Manage")} &quot;
            {product.title.length > 25 ? `${product?.title?.substring(0, 25)}...` : product.title}&quot;
          </h2>
          {product.title.length > 25 && (
            <div className={styles.toolTipAfterEllipsis}>
              <ToolTip text={product.title} orientation="right" />
            </div>
          )}
        </div>
        <img
          src={closeIcon}
          alt="close"
          className={styles.close_icon}
          onClick={modalHandler}
        />
      </div>

      <div className={styles.athletes_container}>
        {athletes && athletes.length > 0 && (
          <div className={styles.tableScrollContainer}>
            <div className={styles.table_header}>
              <div className={styles.purchases_header}>
                <div className={styles.name}>{t('Name')}</div>
                <div className={styles.method}>{t('Method')}</div>
                <div className={styles.collection}>{t('Collection')}</div>
                <div className={styles.purchases}>{t('Purchases')}</div>
                <div className={styles.status}>{t('Status')}</div>
                <div className={styles.actions}>{t('Actions')}</div>
              </div>
            </div>
            <div className={styles.offerings_container}>
              {athletes.map((athlete) => (
                <AthleteListItem
                  key={athlete.studentProfileId || athlete.firstName}
                  athlete={athlete}
                  onMarkAsPaid={handleMarkAsPaid}
                  onMarkAsUnpaid={handleMarkAsUnpaid}
                  markingPaidId={markingPaidId}
                />
              ))}
            </div>
          </div>
        )}
        {/* CFTB-3422: only while there is nothing to show yet. A refetch after
            Mark as paid keeps the existing rows on screen instead of swapping
            the whole table for a spinner. */}
        {loading && (!athletes || athletes.length === 0) && (
          <div className={styles.empty_state}>
            <Spinner />
          </div>
        )}
        {!loading && (!athletes || athletes.length === 0) && (
          <div className={styles.empty_state}>
            <p className={styles.empty_state_title}>{t('No athletes assigned yet.')}</p>
            <p className={styles.empty_state_subtitle}>
              {t('Use the Assign action to assign athletes to this offering.')}
            </p>
          </div>
        )}
      </div>
      <div className={styles.button_wrapper}>
        <Button
          buttonType="secondary-outline"
          title={t("Close")}
          onClickFunc={modalHandler}
        />
      </div>
    </ModalLayout>
  );
};

export default memo(ManageOfferingModal);
